import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { Lock } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Bouton du canevas v0.5.0 (btn) : 44 px, rayon 8 px, IBM Plex Sans 14 px medium, bordure 1 px.
  // Focus : l'anneau commun de tokens.css (2 px sarcelle, décalé de 2 px), sans halo supplémentaire.
  // État refusé (ST_INACTIF, disabled ou aria-disabled) : theme.css — fond neutre, bord à tirets,
  // encre 2, jamais d'opacité ; `disabled:opacity-50` reste pour les écrans qui s'y réfèrent mais
  // est neutralisé par la règle commune.
  // Transition de couleur au SURVOL seulement (FND-V050-164) : un bouton qui s'active au chargement
  // (désactivé → sarcelle) était mesuré par axe à mi-transition, 2,29:1 au lieu de 7,4:1.
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border text-sm font-medium whitespace-nowrap hover:transition-colors outline-none disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // FND-G-V5-C2-01 : la base ne transitionne que les couleurs, jamais l'opacité (axe
        // mesurait un bouton à ~73 % d'opacité pendant la transition d'activation).
        // primary : aplat sarcelle, texte blanc (7,4:1) ; survol : sarcelle foncée.
        default: "border-primary bg-primary text-primary-foreground hover:border-brand-emphasis hover:bg-brand-emphasis",
        // danger : fond panneau, texte et bord brique (8,0:1).
        destructive:
          "border-destructive bg-card text-error-text hover:bg-error-soft",
        // secondary : fond panneau, bord line, encre.
        outline:
          "border-border bg-card text-foreground hover:bg-secondary",
        secondary:
          "border-border bg-card text-foreground hover:bg-secondary",
        // ghost : transparent, texte sarcelle.
        ghost:
          "border-transparent bg-transparent text-brand-text hover:bg-brand-subtle",
        link: "border-transparent text-brand-text underline underline-offset-4 hover:no-underline",
      },
      size: {
        // 44 px : cible du canevas (min-height: 44px) pour toutes les tailles utilisables.
        default: "h-11 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-11 gap-2 px-3 has-[>svg]:px-2",
        lg: "h-11 px-6 has-[>svg]:px-4",
        icon: "size-11",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-11",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// forwardRef : ce projet est en React 18. Les déclencheurs Radix `asChild`
// (DropdownMenuTrigger, TooltipTrigger…) transmettent une ref à leur enfant Button ;
// sans forwardRef, React avertit « Function components cannot be given refs ».
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant = "default", size = "default", asChild = false, children, onClick, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : "button"
  // Action refusée (A11Y-2-01) : `aria-disabled="true"` garde le bouton focalisable ; le clic est
  // intercepté et un cadenas dit le refus (le motif est relié par l'appelant, aria-describedby).
  const refuse = props["aria-disabled"] === true || props["aria-disabled"] === "true"
  const occupe = props["aria-busy"] === true || props["aria-busy"] === "true"
  const garde: React.MouseEventHandler<HTMLButtonElement> | undefined = refuse
    ? (event) => { event.preventDefault(); event.stopPropagation() }
    : onClick

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={garde}
      {...props}
    >
      {asChild || !refuse || occupe ? children : <><Lock aria-hidden="true" data-refus="" />{children}</>}
    </Comp>
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
